import type { ReactNode } from 'react';

/**
 * Presentation shell shared by every fatal-connect screen
 * (Unauthenticated / OriginRejected / VersionMismatch / ServerClosed).
 *
 * Keeps styling consistent with the existing `ConnectionErrorScreen`
 * (full-viewport dark overlay) but free of any retry-loop or Sentry
 * coupling — fatal screens don't retry automatically; the primary
 * action is whatever the caller wires up.
 */
export interface FatalScreenShellProps {
	iconColor: string;
	title: string;
	description: ReactNode;
	primary?: {
		label: string;
		onClick: () => void;
		testId?: string;
	};
	secondary?: {
		label: string;
		onClick: () => void;
		testId?: string;
	};
	footnote?: ReactNode;
}

export function FatalScreenShell({ iconColor, title, description, primary, secondary, footnote }: FatalScreenShellProps) {
	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				background: 'var(--khal-bg)',
				color: 'var(--khal-text-primary)',
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				zIndex: 99999,
				padding: '2rem',
			}}
		>
			<div
				style={{
					width: 64,
					height: 64,
					borderRadius: '50%',
					background: `color-mix(in oklch, ${iconColor} 15%, transparent)`,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					marginBottom: '1.5rem',
				}}
			>
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ stroke: iconColor }} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<circle cx="12" cy="12" r="10" />
					<line x1="12" y1="8" x2="12" y2="12" />
					<line x1="12" y1="16" x2="12.01" y2="16" />
				</svg>
			</div>
			<h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: '0 0 0.5rem 0', color: 'var(--khal-text-primary)' }}>{title}</h1>
			<div
				style={{
					fontSize: '0.9rem',
					color: 'var(--khal-text-muted)',
					margin: '0 0 2rem 0',
					textAlign: 'center',
					maxWidth: '440px',
					lineHeight: 1.5,
				}}
			>
				{description}
			</div>
			<div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
				{primary ? (
					<button
						type="button"
						onClick={primary.onClick}
						data-testid={primary.testId}
						style={{
							padding: '0.6rem 1.5rem',
							borderRadius: '8px',
							border: 'none',
							background: 'var(--khal-accent-blue)',
							color: 'white',
							fontSize: '0.85rem',
							fontWeight: 500,
							cursor: 'pointer',
						}}
					>
						{primary.label}
					</button>
				) : null}
				{secondary ? (
					<button
						type="button"
						onClick={secondary.onClick}
						data-testid={secondary.testId}
						style={{
							padding: '0.6rem 1.5rem',
							borderRadius: '8px',
							border: '1px solid var(--khal-border)',
							background: 'var(--khal-surface)',
							color: 'var(--khal-text-primary)',
							fontSize: '0.85rem',
							fontWeight: 500,
							cursor: 'pointer',
						}}
					>
						{secondary.label}
					</button>
				) : null}
			</div>
			{footnote ? (
				<p style={{ fontSize: '0.75rem', color: 'var(--khal-text-faint)', marginTop: '1.5rem', textAlign: 'center' }}>{footnote}</p>
			) : null}
		</div>
	);
}
